import { format, parseISO } from "date-fns";
import { RTable, Section, SummaryStats, Signatures } from "./ReportSheet";
import { money, qtyFmt, dateTimeFmt } from "../lib/api";

const KEYS = ["sales", "cash_sales", "credit_sales", "collections", "purchases", "supplier_payments", "expenses", "net"];
const H = {
  daily: ["Date", "Total sales", "Cash", "Credit", "Collected", "Purchases", "Paid suppliers", "Expenses", "Net cash"],
  top: ["SKU", "Product", "Qty sold", "Total"],
  category: ["Category", "Amount"],
  depot: ["Depot", "Total sales", "Credit sales", "Collected", "Purchases", "Expenses", "Net cash"],
  debtors: ["Customer", "Depot", "Credit", "Paid", "Balance"],
  creditors: ["Supplier", "Depot", "On credit", "Paid", "Balance", "Next due", "Status"],
};
const TOTAL = { date: "total" };

export function PeriodSheet({ r }) {
  const s = r.summary;
  const title = r.period === "week" ? "Weekly Summary" : "Monthly Summary";
  const dailyCells = (d) => (d === TOTAL ? [<b key="t">TOTAL</b>, ...KEYS.map((k) => <b key={k}>{money(s[k])}</b>)]
    : [format(parseISO(d.date), "EEE, MMM d"), ...KEYS.map((k) => money(d[k]))]);
  return (
    <div className="print-sheet rise mx-auto max-w-[980px] space-y-6 rounded-lg bg-white p-5 text-slate-900 shadow-2xl sm:p-10" data-testid="period-report-sheet">
      <header className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-600">MATWIGA ENTERPRISES · {r.period === "week" ? "Week" : "Month"} in review</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight" data-testid="period-report-title">{title}</h2>
        </div>
        <div className="text-sm sm:text-right space-y-0.5">
          <div className="font-semibold" data-testid="period-depot-label">{r.depot_label}</div>
          <div data-testid="period-range">{format(parseISO(r.start), "MMM d")} – {format(parseISO(r.end), "MMM d, yyyy")}</div>
          <div className="text-xs text-slate-500">Generated by {r.generated_by} · {dateTimeFmt(r.generated_at)}</div>
        </div>
      </header>

      <SummaryStats s={s} netLabel="Net cash" />

      <Section title={`Daily breakdown · ${qtyFmt(s.units_sold)} units sold`}>
        <RTable testid="period-daily-table" headers={H.daily} items={[...r.daily, TOTAL]} rowKey={(d) => d.date} cells={dailyCells} />
      </Section>

      <Section title="Top products sold">
        <RTable testid="period-top-products-table" headers={H.top} items={r.top_products} rowKey={(i) => i.sku}
          cells={(i) => [i.sku, i.name, `${qtyFmt(i.qty)} ${i.unit}`, money(i.total)]} />
      </Section>

      <Section title="Expenses by category">
        <RTable headers={H.category} items={Object.entries(r.by_category)} rowKey={([k]) => k} cells={([k, v]) => [k, money(v)]} />
      </Section>

      {r.breakdown.length > 0 && (
        <Section title="Depot breakdown">
          <RTable testid="period-depot-table" headers={H.depot} items={r.breakdown} rowKey={(b) => b.depot_name}
            cells={(b) => [b.depot_name, money(b.sales), money(b.credit_sales), money(b.collections), money(b.purchases), money(b.expenses), money(b.net)]} />
        </Section>
      )}

      <Section title={`Outstanding customer credit · ${money(s.outstanding_credit)}`}>
        <RTable testid="period-debtors-table" headers={H.debtors} items={r.debtors} rowKey={(d) => `${d.customer}-${d.depot_name}`}
          cells={(d) => [d.customer, d.depot_name, money(d.total_credit), money(d.total_paid), money(d.balance)]} />
      </Section>

      <Section title={`Owed to suppliers · ${money(s.outstanding_payables)}`}>
        <RTable testid="period-creditors-table" headers={H.creditors} items={r.creditors || []} rowKey={(c) => `${c.supplier}-${c.depot_name}`}
          cells={(c) => [c.supplier, c.depot_name, money(c.total_credit), money(c.total_paid), money(c.balance), c.next_due || "—", c.status.replace("_", " ")]} />
      </Section>

      <Signatures />
    </div>
  );
}
